'use es6';

import { useEffect, useMemo, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { SideNav } from 'foundations-components/transitional/components/SideNav';
import { View } from 'foundations-components/components/View';
import { useUpgradePoint } from 'ui-addon-upgrades/ums/useUpgradePoint';
import { Flex } from 'foundations-components/components/Flex';
import { UpgradeIcon } from 'foundations-assets';
import { clearReportsFilters } from '../actions/reportsActions';
import { COLLECTION_ACCESS } from '../constants/accessControl';
import { trackEvent, EVENTS } from '../lib/usageTracker';
import SideNavReRenderContext from '../records/SideNavReRenderContext';
import { NAV_ITEM_HIERARCHY_ARRAY, getAnalyticsToolsIA } from '../config/analyticTools';
import { selectedRowsActions } from '../redux/selectedRows';
import { getGatesAndScopes } from '../selectors/gatesSelector';
import { getCollectionsPath, getTemplatesPath } from '../utils/urls';
import { containsActiveChild } from '../utils/reportingIAUtils';
import { useAnalyticsPermissions } from '../utils/useAnalyticsPermissions';
import { useActiveAnalyticsPage } from '../utils/useActiveAnalyticsPage';
import NavItem from '../components/sidenav/NavItem';
import AnalyticsBadge from '../components/common/AnalyticsBadge';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LockedSideNavItem = ({
  level,
  collectionId,
  active,
  upgradePoint
}) => {
  const {
    openUpgradePage
  } = useUpgradePoint(upgradePoint);
  const handleUpgradePoint = () => {
    if (upgradePoint) {
      openUpgradePage();
    }
  };
  useEffect(() => {
    if (active) {
      handleUpgradePoint();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  return /*#__PURE__*/_jsx(NavItem, {
    testId: `${collectionId}-nav-item-restricted`,
    value: collectionId,
    title: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      marginInlineStart: `${level * 300}`,
      gap: "200",
      inlineSize: "100%",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: `reports.sidebar.collections.${collectionId}`
      }), /*#__PURE__*/_jsx(UpgradeIcon, {
        color: 'fill-primary-default'
      })]
    }),
    onClick: handleUpgradePoint
  });
};

// this should have unit tests
export const findParentNavItem = (items, targetId) => items.find(navItem => {
  const itemId = navItem.get('id');
  if (itemId === targetId) {
    return true;
  }
  const children = navItem.get('children');
  return children && findParentNavItem(children, targetId);
});
const TemplatesSideBar = ({
  showGlobalSearchPage
}) => {
  const {
    sideNavReRender
  } = useContext(SideNavReRenderContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    gates,
    scopes
  } = useSelector(getGatesAndScopes);
  const analyticsPermissions = useAnalyticsPermissions();
  const activeIA = getAnalyticsToolsIA(gates, scopes);
  const activeNavItemId = useActiveAnalyticsPage();
  const activeCollectionId = useMemo(() => {
    if (activeNavItemId) {
      const activeCollectionNavItem = findParentNavItem(activeIA, activeNavItemId);
      return activeCollectionNavItem ? activeCollectionNavItem.get('id') : null;
    }
    return null;
  }, [activeNavItemId, activeIA]);
  const handleClick = (sideNavItem, childrenIA) => {
    const parentElement = findParentNavItem(activeIA, sideNavItem).get('id');
    dispatch(clearReportsFilters());
    dispatch(selectedRowsActions.clearSelectedRows());
    const hasParentElement = parentElement !== sideNavItem;
    const getPath = !childrenIA ? getTemplatesPath(parentElement, hasParentElement && sideNavItem) : getCollectionsPath(parentElement, hasParentElement && sideNavItem);
    return history.push(getPath);
  };

  // levels 1-3 in order of top to lower level nav item types
  const renderSideNavItem = (navItemIA, level) => {
    const navItemId = navItemIA.get('id');
    const accessControl = COLLECTION_ACCESS.get(navItemId);
    if (accessControl) {
      const accessAllowed = analyticsPermissions.getIn([navItemId, 'accessAllowed']);
      const isVisible = analyticsPermissions.getIn([navItemId, 'isVisible']);
      if (!isVisible) {
        return null;
      }
      if (!accessAllowed) {
        return /*#__PURE__*/_jsx(LockedSideNavItem, {
          level: level,
          collectionId: navItemId,
          active: navItemId === activeCollectionId,
          upgradePoint: COLLECTION_ACCESS.getIn([navItemId, 'upgradePoint'])
        }, navItemId);
      }
    }
    const childrenIA = navItemIA.get('children');
    const hasChildren = childrenIA && childrenIA.length;

    // if suite has no visible children, don't render it
    if (hasChildren) {
      const hasVisibleChildren = childrenIA.some(child => {
        const childId = child.get('id');
        const childAccessControl = COLLECTION_ACCESS.get(childId);
        return childAccessControl ? analyticsPermissions.getIn([childId, 'isVisible']) : true;
      });
      if (!hasVisibleChildren) {
        return null;
      }
    }
    const Title = /*#__PURE__*/_jsxs(Flex, {
      justifyContent: "space-between",
      inlineSize: "100%",
      alignItems: "center",
      paddingInlineEnd: "300",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: hasChildren ? `reports.sidebar.collections.${navItemId}` : `reports.suiteContents.${navItemId}.title`
      }), /*#__PURE__*/_jsx(AnalyticsBadge, {
        navItemId: navItemId
      })]
    });
    const nextLevel = level + 1;
    const navClickSharedTrackingProps = {
      templateKey: navItemId,
      actionType: NAV_ITEM_HIERARCHY_ARRAY[level - 1],
      filterValue: level
    };
    return /*#__PURE__*/_jsx(NavItem, {
      title: Title,
      active: navItemId === activeNavItemId && !showGlobalSearchPage,
      level: level,
      defaultOpen: containsActiveChild(navItemIA, activeNavItemId),
      onOpenChange: openOrClose => {
        trackEvent(EVENTS.clickCollectionNavItem, Object.assign({
          action: openOrClose
        }, navClickSharedTrackingProps));
      },
      onClick: () => {
        trackEvent(EVENTS.clickCollectionNavItem, Object.assign({
          action: 'pageView'
        }, navClickSharedTrackingProps));
        return handleClick(navItemId, childrenIA);
      },
      testId: `${navItemId}-nav-item`,
      children: childrenIA && childrenIA.map(childNavItemIA => renderSideNavItem(childNavItemIA, nextLevel))
    }, navItemId);
  };
  return /*#__PURE__*/_jsx(SideNav, {
    "aria-label": I18n.text('reports.sidebar.betaToolsHeader'),
    headingText: /*#__PURE__*/_jsx(View, {
      id: 'collections-sidebar',
      marginBlockStart: '0',
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reports.sidebar.betaToolsHeader"
      })
    }),
    marginBlockStart: "0 !important",
    divider: true,
    children: activeIA.map(collectionNavItem => renderSideNavItem(collectionNavItem, 1))
  }, sideNavReRender);
};
export default TemplatesSideBar;